import { useEffect, useState } from 'react';
import { Building2, Check } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';

const SWITCH_ROLES = new Set(['manager', 'admin', 'super_admin', 'owner']);
export const POS_ACTIVE_BRANCH_KEY = 'pos_active_branch_id';

/**
 * iter957 — POS header Branch Picker. Lets permitted staff explicitly set which
 * branch the till is "acting as". The choice persists in localStorage
 * (`pos_active_branch_id`) — the same key the stock-request popup reads first —
 * so cross-branch stock alerts route to the selected branch. Read-only staff
 * (cashiers) just see their fixed branch name.
 */
export function POSBranchPicker({ userRole = 'cashier', homeBranchId = null, value, onChange }) {
  const [branches, setBranches] = useState([]);
  const canSwitch = SWITCH_ROLES.has((userRole || '').toLowerCase());

  useEffect(() => {
    let alive = true;
    api.get('/branches')
      .then(({ data }) => { if (alive) setBranches(data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const current = value || homeBranchId || '';
  const currentName = branches.find((b) => b.id === current)?.name || 'Select branch';

  const handleChange = (id) => {
    try { localStorage.setItem(POS_ACTIVE_BRANCH_KEY, id); } catch { /* ignore */ }
    onChange && onChange(id);
  };

  // Cashiers (no switch permission): show a static branch chip only.
  if (!canSwitch) {
    if (!current) return null;
    return (
      <span
        data-testid="pos-branch-static"
        className="inline-flex items-center gap-1.5 h-10 rounded-xl border border-border bg-muted/40 px-2.5 text-xs font-medium text-muted-foreground"
        title="Your branch"
      >
        <Building2 size={13} /> {currentName}
      </span>
    );
  }

  return (
    <Select value={current} onValueChange={handleChange}>
      <SelectTrigger
        className="h-10 w-auto min-w-[120px] gap-1.5 rounded-xl border-primary/30 bg-primary/5 text-xs font-semibold text-primary"
        data-testid="pos-branch-picker"
        title="Set which branch this till is acting as"
      >
        <Building2 size={14} />
        <SelectValue placeholder="Select branch" />
      </SelectTrigger>
      <SelectContent data-testid="pos-branch-picker-menu">
        {branches.map((b) => (
          <SelectItem key={b.id} value={b.id} data-testid={`pos-branch-opt-${b.id}`}>
            <span className="flex items-center gap-2">
              {b.id === current && <Check size={12} className="text-primary" />}
              {b.name}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default POSBranchPicker;
